// 📄 services/miEspacioCompartidoService.ts
// Espejo de solo lectura de una NotaSap cuando el autor marca
// `compartirConProyecto: true` (ver NotasSapSection.tsx) — vive en
// projects/{projectId}/notasSapCompartidas/{mismoId}, FUERA del árbol
// usuarios/{uid}/..., que es 100% privado por dueño (ver firestore.rules).
// La nota privada del autor sigue siendo la fuente de verdad; esto es una
// copia denormalizada para que el resto del equipo pueda leerla sin
// necesitar acceso al espacio privado de nadie. Se sincroniza a mano
// (share/unshare) en vez de con un trigger de Cloud Functions porque el
// volumen es bajo y así no se agrega infraestructura nueva para esto.
import { doc, deleteDoc, setDoc, collection, getDocs } from "firebase/firestore";
import { db } from '../firebase';
import { NotaSapData, NotaSapCompartida } from '../types/miEspacio.ts';

const notasCompartidasRef = (projectId: string) => collection(db, 'projects', projectId, 'notasSapCompartidas');
const notaCompartidaRef = (projectId: string, id: string) => doc(db, 'projects', projectId, 'notasSapCompartidas', id);

export const shareNotaSap = async (
  projectId: string,
  id: string,
  data: NotaSapData,
  autorUid: string,
  autorEmail: string
): Promise<void> => {
  await setDoc(notaCompartidaRef(projectId, id), {
    autorUid,
    autorEmail,
    titulo: data.titulo.trim(),
    numeroONota: data.numeroONota.trim(),
    modulo: data.modulo,
    causa: data.causa.trim(),
    solucion: data.solucion.trim(),
    tipoNota: data.tipoNota || '',
    sistemaAfectado: data.sistemaAfectado || '',
    campoTecnico: (data.campoTecnico || '').trim(),
    tags: data.tags || [],
    versionAmbiente: (data.versionAmbiente || '').trim(),
    fechaVerificacion: data.fechaVerificacion || null
  });
};

// OJO al llamar esto: la regla de Firestore para `delete` exige
// `resource.data.autorUid` — si el documento nunca se creó (nota nunca
// compartida), `resource` es null y la regla deniega con "Missing or
// insufficient permissions" en vez de no-opear. El caller es responsable
// de invocar esto SOLO cuando sabe que el espejo existe (ver
// `eraCompartidaAlAbrir` en NotasSapSection.tsx); no hay forma de hacerlo
// incondicional y silencioso sin abrir la regla de lectura a cualquiera.
export const unshareNotaSap = async (projectId: string, id: string): Promise<void> => {
  await deleteDoc(notaCompartidaRef(projectId, id));
};

export const getNotasSapCompartidas = async (projectId: string): Promise<NotaSapCompartida[]> => {
  const snap = await getDocs(notasCompartidasRef(projectId));
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<NotaSapCompartida, 'id'>) }));
};
